import { Check } from 'lucide-react'
import { useState } from 'react'

import { useDevanningState } from '../../stores/devanningStore'
import {
  useScanProgressActions,
  useScanProgressState
} from '../../stores/scanProgressStore'

import PalletPrint, { PalletPrintData } from './PalletPrint'

import { inboundV2Service } from '@/services'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/useUIStore'

const MixedPallet: React.FC = () => {
  const { progress, cargoMarksGroup } = useScanProgressState()
  const { fetchProgress } = useScanProgressActions()
  const { masterBill } = useDevanningState()
  const { showError, showSuccess } = useUIStore()
  const [selectedMarkIds, setSelectedMarkIds] = useState<number[]>([])
  const [showPrintDialog, setShowPrintDialog] = useState(false)
  const [printData, setPrintData] = useState<PalletPrintData | null>(null)
  const { user } = useAuthStore()

  // 切换mark选中状态
  const toggleMarkSelection = (markId: number) => {
    setSelectedMarkIds(prev =>
      prev.includes(markId)
        ? prev.filter(id => id !== markId)
        : [...prev, markId]
    )
  }

  // 全选/全不选
  const toggleSelectAll = (allMarkIds: number[]) => {
    setSelectedMarkIds(prev =>
      prev.length === allMarkIds.length ? [] : allMarkIds
    )
  }

  const handleConfirmMixedPallet = async () => {
    if (!masterBill || !progress || selectedMarkIds.length === 0) {
      showError('请选择至少一个唛头进行打托')
      return
    }

    if (selectedMarkIds.length < 2) {
      showError('混合打托需要选择至少两个唛头')
      return
    }

    const { success, message, data } = await inboundV2Service.createMixedPallet(masterBill.id, {
      houseBillId: progress.houseBillId,
      markIds: selectedMarkIds
    })

    if (success) {
      showSuccess(message)
      // 清空选择
      setSelectedMarkIds([])
      // 更新进度数据
      await fetchProgress(progress.markId)
      // 准备打印数据
      if (data?.palletCode) {
        setPrintData({
          customerNumber: masterBill?.customerNumber,
          houseBlNumber: data.houseBillNumber,
          devanDate: masterBill?.devanDate || '',
          palletCode: data.palletCode,
          total: data.palletTotalPackages,
          cargoMarks: data.cargoMarks,
          notes: '混',
          printUser: user?.name || user?.nick || '名無し'
        })
        setShowPrintDialog(true)
      }
    } else {
      showError(message)
    }
  }

  return (
    <>
      {cargoMarksGroup
        .filter((group) => group.houseBlNumber === progress?.houseBillNumber)
        .map((group) => {
          const mixedMarks = group.marks.filter((mark) => mark.canMixedPallet && !mark.isCompleted)

          if (mixedMarks.length < 2 ) return null

          const allMarkIds = mixedMarks.map(mark => mark.markId)
          const isAllSelected = selectedMarkIds.length === allMarkIds.length && allMarkIds.every(id => selectedMarkIds.includes(id))

          return (
            <div
              key={group.houseBillId}
              className="pt-4 border-t border-gray-200"
            >
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4">
                <div className="space-y-3">
                  {/* 标题和全选 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm">
                        可混合打托 Marks
                      </span>
                      {mixedMarks.length > 1 && (
                        <button
                          onClick={() => toggleSelectAll(allMarkIds)}
                          className="text-xs text-amber-700 hover:text-amber-800 underline"
                        >
                          {isAllSelected ? '全不选' : '全选'}
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      已选择 {selectedMarkIds.length}/{mixedMarks.length} 个
                    </div>
                  </div>

                  {/* 唛头选择列表 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {mixedMarks.map((mark) => {
                      const isSelected = selectedMarkIds.includes(mark.markId)
                      return (
                        <div
                          key={mark.markId}
                          onClick={() => toggleMarkSelection(mark.markId)}
                          className={`
                            relative flex items-center justify-between p-2 rounded-md cursor-pointer transition-all duration-150
                            ${isSelected
                              ? 'bg-amber-200 border-2 border-amber-400 shadow-sm'
                              : 'bg-amber-100 border-2 border-transparent hover:border-amber-300'}
                          `}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`
                              w-4 h-4 rounded border-2 flex items-center justify-center transition-colors
                              ${isSelected ? 'bg-amber-600 border-amber-600' : 'border-amber-400 bg-white'}
                            `}>
                              {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            <span className="text-sm font-medium text-amber-900">
                              {mark.markNumber}
                            </span>
                          </div>
                          <span className="text-xs text-amber-700">
                            {mark.summary.scanned}件
                          </span>
                        </div>
                      )
                    })}
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleConfirmMixedPallet}
                      disabled={selectedMarkIds.length < 2}
                      className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed transition-colors"
                    >
                      混合打托 ({selectedMarkIds.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}

      {/* 打印对话框 */}
      {showPrintDialog && printData && (
        <PalletPrint
          data={printData}
          visible={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
        />
      )}
    </>
  )
}

export default MixedPallet
